import { useState, useEffect, useCallback } from 'react';
import type { VocabWord } from '../types';

const STORAGE_KEY = 'vocabulizer_word_lists';

export interface SavedWordList {
  id: string;
  title: string;
  author: string;
  gradeLevel: string;
  subject: string;
  words: VocabWord[];
  createdAt: number;
  updatedAt: number;
}

export interface SavedWordListWithKey extends SavedWordList {
  key: string;
}

interface WordListsState {
  [key: string]: SavedWordList;
}

/**
 * Manages saved word lists in localStorage.
 * Lists are keyed by "title — author" for easy reuse across sessions.
 *
 * Schema:
 * {
 *   [key: string]: {
 *     id: string,
 *     title: string,
 *     author: string,
 *     gradeLevel: string,
 *     subject: string,
 *     words: VocabWord[],
 *     createdAt: number,
 *     updatedAt: number,
 *   }
 * }
 */
export function useWordLists() {
  const [lists, setLists] = useState<WordListsState>({});

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setLists(JSON.parse(stored) as WordListsState);
    } catch {
      // Ignore parse errors
    }
  }, []);

  const persist = useCallback((updatedLists: WordListsState): void => {
    setLists(updatedLists);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedLists));
    } catch (err) {
      console.warn('Could not save word lists to localStorage:', err);
    }
  }, []);

  /** Create a list key from title + author */
  const makeKey = (title: string, author?: string): string =>
    `${(title || 'Untitled').trim()} — ${(author || 'Unknown').trim()}`;

  /** Save or update a word list */
  const saveList = useCallback(
    ({ title, author, gradeLevel, subject, words }: { title: string; author?: string; gradeLevel: string; subject: string; words: VocabWord[] }): string => {
      const key = makeKey(title, author);
      const now = Date.now();
      const existing = lists[key];
      const updated: WordListsState = {
        ...lists,
        [key]: {
          id: existing?.id || now.toString(),
          title: title || 'Untitled',
          author: author || '',
          gradeLevel,
          subject,
          words,
          createdAt: existing?.createdAt || now,
          updatedAt: now,
        },
      };
      persist(updated);
      return key;
    },
    [lists, persist]
  );

  /** Delete a word list by key */
  const deleteList = useCallback(
    (key: string): void => {
      const updated = { ...lists };
      delete updated[key];
      persist(updated);
    },
    [lists, persist]
  );

  /** Update the title of a word list */
  const updateTitle = useCallback(
    (oldKey: string, newTitle: string): string => {
      const list = lists[oldKey];
      if (!list) return oldKey;

      const trimmedTitle = (newTitle || 'Untitled').trim();
      const newKey = makeKey(trimmedTitle, list.author);

      // If title didn't actually change (same key), just return
      if (oldKey === newKey) return oldKey;

      // Create new entry with updated title
      const updated: WordListsState = { ...lists };
      delete updated[oldKey];
      updated[newKey] = {
        ...list,
        title: trimmedTitle,
        updatedAt: Date.now(),
      };

      persist(updated);
      return newKey;
    },
    [lists, persist]
  );

  /** Find a list by title + author */
  const findList = useCallback(
    (title: string, author?: string): SavedWordList | null => {
      const key = makeKey(title, author);
      return lists[key] || null;
    },
    [lists]
  );

  /** Get all lists as a sorted array (most recently updated first) */
  const allLists: SavedWordListWithKey[] = Object.entries(lists)
    .map(([key, value]) => ({ key, ...value }))
    .sort((a, b) => b.updatedAt - a.updatedAt);

  return {
    lists,
    allLists,
    saveList,
    deleteList,
    updateTitle,
    findList,
    makeKey,
  };
}
